/** Fetch wrapper for the GrimoireCodexDB API. Same-origin, cookie-authenticated, JSON both ways. */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly details?: unknown,
  ) {
    super(message)
  }

  /** Field-level validation messages keyed by dotted path (`systems.0`, `year`). */
  get fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {}
    return Object.fromEntries(
      (this.details as Array<{ path: string; message: string }>).map((d) => [d.path, d.message]),
    )
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers)
  let body = init.body
  if (init.json !== undefined) {
    headers.set('content-type', 'application/json')
    body = JSON.stringify(init.json)
  }
  headers.set('accept', 'application/json')
  let res: Response
  try {
    res = await fetch(path, { ...init, headers, body, credentials: 'same-origin' })
  } catch {
    throw new ApiError(0, 'Could not reach the server. Check your connection and try again.')
  }
  const text = await res.text()
  const data = text ? safeJson(text) : null
  if (!res.ok) {
    const err = data as { error?: string; details?: unknown } | null
    throw new ApiError(res.status, err?.error ?? `Request failed (${res.status})`, err?.details)
  }
  return data as T
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | undefined | null>): string {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    sp.set(k, v === true ? '1' : String(v))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const coverUrl = (key: string | null | undefined) => (key ? `/${key}` : null)
